import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { PageTitle } from "../../../_metronic/layout/core";

interface ReportItem {
  id: string;
  title: string;
  description: string;
  path: string;
  icon: React.ReactNode;
}

interface ReportSection {
  id: string;
  title: string;
  color: string;
  bgColor: string;
  backgroundSvg: React.ReactNode;
  reports: ReportItem[];
}

interface ReportUsage {
  [reportId: string]: number;
}

const STORAGE_KEY = "trackvan_report_usage";

const reportSections: ReportSection[] = [
  {
    id: "treatment-analysis",
    title: "Treatment Analysis",
    color: "#009EF7",
    bgColor: "#F1FAFF",
    backgroundSvg: (
      <svg viewBox="0 0 400 200" fill="none" style={{ width: "350px", height: "180px", opacity: 0.08 }}>
        <circle cx="320" cy="40" r="80" fill="currentColor"/>
        <circle cx="380" cy="120" r="60" fill="currentColor"/>
        <circle cx="250" cy="150" r="40" fill="currentColor"/>
        <rect x="50" y="80" width="30" height="100" rx="4" fill="currentColor"/>
        <rect x="90" y="50" width="30" height="130" rx="4" fill="currentColor"/>
        <rect x="130" y="30" width="30" height="150" rx="4" fill="currentColor"/>
        <rect x="170" y="60" width="30" height="120" rx="4" fill="currentColor"/>
        <path d="M65 70L105 40L145 20L185 50" stroke="currentColor" strokeWidth="4" strokeLinecap="round"/>
      </svg>
    ),
    reports: [
      {
        id: "treatments-by-advisor",
        title: "Treatments by Advisor",
        description: "View which advisors recommend which treatments",
        path: "/reports/treatments-by-advisor",
        icon: (
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
            <circle cx="9" cy="7" r="4" stroke="currentColor" strokeWidth="1.5"/>
            <path d="M3 21v-2a4 4 0 014-4h4a4 4 0 014 4v2" stroke="currentColor" strokeWidth="1.5"/>
            <path d="M16 3.13a4 4 0 010 7.75" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
            <path d="M21 21v-2a4 4 0 00-3-3.87" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
          </svg>
        ),
      },
      {
        id: "treatments-by-supervisor",
        title: "Treatments by Supervisor",
        description: "Treatment activity under each supervisor",
        path: "/reports/treatments-by-supervisor",
        icon: (
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
            <path d="M16 21v-2a4 4 0 00-4-4H6a4 4 0 00-4 4v2" stroke="currentColor" strokeWidth="1.5"/>
            <circle cx="9" cy="7" r="4" stroke="currentColor" strokeWidth="1.5"/>
            <path d="M17 11l2 2 4-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        ),
      },
      {
        id: "treatments-by-branch",
        title: "Treatments by Branch",
        description: "Treatment usage across branch locations",
        path: "/reports/treatments-by-branch",
        icon: (
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
            <path d="M3 21h18M3 7v14M21 7v14M6 11h4M6 15h4M14 11h4M14 15h4M10 21V17h4v4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M3 7l9-4 9 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        ),
      },
      {
        id: "treatments-by-dealer",
        title: "Treatments by Dealer",
        description: "Treatment distribution across dealerships",
        path: "/reports/treatments-by-dealer",
        icon: (
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
            <path d="M21 16V8a2 2 0 00-1-1.73l-7-4a2 2 0 00-2 0l-7 4A2 2 0 003 8v8a2 2 0 001 1.73l7 4a2 2 0 002 0l7-4A2 2 0 0021 16z" stroke="currentColor" strokeWidth="1.5"/>
            <path d="M3.27 6.96L12 12.01l8.73-5.05M12 22.08V12" stroke="currentColor" strokeWidth="1.5"/>
          </svg>
        ),
      },
      {
        id: "most-used-treatments",
        title: "Most Used Treatments",
        description: "Ranking of treatments by popularity",
        path: "/reports/most-used-treatments",
        icon: (
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
            <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        ),
      },
    ],
  },
  {
    id: "operations-performance",
    title: "Operations & Performance",
    color: "#50CD89",
    bgColor: "#E8FFF3",
    backgroundSvg: (
      <svg viewBox="0 0 400 200" fill="none" style={{ width: "350px", height: "180px", opacity: 0.08 }}>
        <circle cx="350" cy="100" r="90" fill="currentColor"/>
        <circle cx="250" cy="30" r="40" fill="currentColor"/>
        <circle cx="180" cy="150" r="60" fill="currentColor"/>
        <path d="M80 180L120 120L160 140L200 80L240 100L280 40" stroke="currentColor" strokeWidth="6" strokeLinecap="round" strokeLinejoin="round"/>
        <circle cx="80" cy="180" r="8" fill="currentColor"/>
        <circle cx="120" cy="120" r="8" fill="currentColor"/>
        <circle cx="160" cy="140" r="8" fill="currentColor"/>
        <circle cx="200" cy="80" r="8" fill="currentColor"/>
      </svg>
    ),
    reports: [
      {
        id: "treatment-category-analysis",
        title: "Treatment Category Analysis",
        description: "HVT vs LVT breakdown",
        path: "/reports/treatment-category-analysis",
        icon: (
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
            <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.5"/>
            <path d="M12 3v9l6 3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
          </svg>
        ),
      },
      {
        id: "job-card-summary",
        title: "Job Card Summary",
        description: "Job card status overview",
        path: "/reports/job-card-summary",
        icon: (
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
            <rect x="4" y="2" width="16" height="20" rx="2" stroke="currentColor" strokeWidth="1.5"/>
            <path d="M8 6h8M8 10h8M8 14h5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
          </svg>
        ),
      },
      {
        id: "advisor-performance",
        title: "Advisor Performance",
        description: "Advisor productivity metrics",
        path: "/reports/advisor-performance",
        icon: (
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
            <path d="M3 3v18h18" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
            <path d="M7 14l4-4 4 4 5-5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M17 9h3v3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        ),
      },
      {
        id: "revenue-analysis",
        title: "Revenue Analysis",
        description: "Customer revenue by branch using segment pricing",
        path: "/reports/revenue-analysis",
        icon: (
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
            <path d="M12 2v20M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        ),
      },
      {
        id: "vehicle-segment-analysis",
        title: "Vehicle Segment Analysis",
        description: "Treatment distribution across vehicle segments",
        path: "/reports/vehicle-segment-analysis",
        icon: (
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
            <rect x="3" y="3" width="7" height="7" rx="1" stroke="currentColor" strokeWidth="1.5"/>
            <rect x="14" y="3" width="7" height="7" rx="1" stroke="currentColor" strokeWidth="1.5"/>
            <rect x="3" y="14" width="7" height="7" rx="1" stroke="currentColor" strokeWidth="1.5"/>
            <rect x="14" y="14" width="7" height="7" rx="1" stroke="currentColor" strokeWidth="1.5"/>
          </svg>
        ),
      },
      {
        id: "deleted-treatments",
        title: "Deleted Treatments",
        description: "Treatments removed from job cards",
        path: "/reports/deleted-treatments",
        icon: (
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
            <path d="M3 6h18M8 6V4a2 2 0 012-2h4a2 2 0 012 2v2M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M10 11v6M14 11v6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
          </svg>
        ),
      },
      {
        id: "daily-job-card-volume",
        title: "Daily Job Card Volume",
        description: "Day-by-day job card creation trend",
        path: "/reports/daily-job-card-volume",
        icon: (
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
            <rect x="3" y="4" width="18" height="18" rx="2" stroke="currentColor" strokeWidth="1.5"/>
            <path d="M16 2v4M8 2v4M3 10h18" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
            <path d="M8 14h.01M12 14h.01M16 14h.01M8 18h.01M12 18h.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
          </svg>
        ),
      },
      {
        id: "water-wash-analysis",
        title: "Water Wash Analysis",
        description: "Water wash counts with repeat wash tracking",
        path: "/reports/water-wash-analysis",
        icon: (
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
            <path d="M12 2.69l5.66 5.66a8 8 0 11-11.31 0z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        ),
      },
      {
        id: "vehicle-model",
        title: "Vehicle Model Report",
        description: "Which vehicle model uses which treatment",
        path: "/reports/vehicle-model",
        icon: (
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
            <path d="M5 17h14M7.5 17l1-7h7l1 7M6 10l1.5-4h9L18 10" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            <circle cx="7.5" cy="17" r="1.5" stroke="currentColor" strokeWidth="1.5"/>
            <circle cx="16.5" cy="17" r="1.5" stroke="currentColor" strokeWidth="1.5"/>
          </svg>
        ),
      },
      {
        id: "water-wash-list",
        title: "Water Wash List",
        description: "Detailed list of all water wash records",
        path: "/reports/water-wash-list",
        icon: (
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
            <path d="M12 2.69l5.66 5.66a8 8 0 11-11.31 0z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M8 14h8M8 18h5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
          </svg>
        ),
      },
      {
        id: "treatment-billing",
        title: "Treatment Billing",
        description: "Detailed treatment billing with per-treatment pricing",
        path: "/reports/treatment-billing",
        icon: (
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
            <rect x="4" y="2" width="16" height="20" rx="2" stroke="currentColor" strokeWidth="1.5"/>
            <path d="M12 6v4M10 8h4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
            <path d="M8 13h8M8 16h5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
          </svg>
        ),
      },
      {
        id: "treatment-monthly-view-dp",
        title: "Monthly View - Dealer Price",
        description: "Pivot table of treatments and dealer price revenue by branch per day",
        path: "/reports/treatment-monthly-view-dp",
        icon: (
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
            <rect x="3" y="3" width="18" height="18" rx="2" stroke="currentColor" strokeWidth="1.5"/>
            <path d="M3 9h18M3 15h18M9 3v18M15 3v18" stroke="currentColor" strokeWidth="1.5"/>
          </svg>
        ),
      },
      {
        id: "treatment-monthly-view-cp",
        title: "Monthly View - Customer Price",
        description: "Pivot table of treatments and customer price revenue by branch per day",
        path: "/reports/treatment-monthly-view-cp",
        icon: (
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
            <rect x="3" y="3" width="18" height="18" rx="2" stroke="currentColor" strokeWidth="1.5"/>
            <path d="M3 9h18M3 15h18M9 3v18M15 3v18" stroke="currentColor" strokeWidth="1.5"/>
          </svg>
        ),
      },
    ],
  },
];

// Helper to get all reports flattened with their section info
const getAllReports = () => {
  const allReports: Array<ReportItem & { sectionColor: string; sectionBgColor: string }> = [];
  reportSections.forEach((section) => {
    section.reports.forEach((report) => {
      allReports.push({
        ...report,
        sectionColor: section.color,
        sectionBgColor: section.bgColor,
      });
    });
  });
  return allReports;
};

const useIsDarkMode = () => {
  const [isDark, setIsDark] = useState(
    document.documentElement.getAttribute("data-bs-theme") === "dark"
  );
  useEffect(() => {
    const observer = new MutationObserver(() => {
      setIsDark(document.documentElement.getAttribute("data-bs-theme") === "dark");
    });
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ["data-bs-theme"] });
    return () => observer.disconnect();
  }, []);
  return isDark;
};

const AllReportsPage: React.FC = () => {
  const navigate = useNavigate();
  const isDark = useIsDarkMode();
  const [usageData, setUsageData] = useState<ReportUsage>({});
  const [topReportIds, setTopReportIds] = useState<string[]>([]);
  const [searchTerm, setSearchTerm] = useState<string>("");

  // Load usage data from localStorage
  const loadUsageData = useCallback(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      try {
        const parsed = JSON.parse(stored) as ReportUsage;
        setUsageData(parsed);
      } catch (e) {
        console.error("Failed to parse report usage data", e);
      }
    }
  }, []);

  // Load on mount
  useEffect(() => {
    loadUsageData();
  }, [loadUsageData]);

  // Refresh usage data when window regains focus or visibility changes
  useEffect(() => {
    const handleFocus = () => loadUsageData();
    const handleVisibility = () => {
      if (document.visibilityState === "visible") {
        loadUsageData();
      }
    };

    window.addEventListener("focus", handleFocus);
    document.addEventListener("visibilitychange", handleVisibility);

    return () => {
      window.removeEventListener("focus", handleFocus);
      document.removeEventListener("visibilitychange", handleVisibility);
    };
  }, [loadUsageData]);

  // Calculate top 5 reports whenever usage data changes
  useEffect(() => {
    const sortedReports = Object.entries(usageData)
      .filter(([, count]) => count > 0)
      .sort(([, a], [, b]) => b - a)
      .slice(0, 5)
      .map(([id]) => id);
    setTopReportIds(sortedReports);
  }, [usageData]);

  // Handle report click - navigate only (tracking happens in GenericReport)
  const handleReportClick = useCallback(
    (path: string) => {
      navigate(path, { state: { from: "/reports" } });
    },
    [navigate]
  );

  // Check if report is in top 5
  const isTopReport = useCallback(
    (reportId: string) => topReportIds.includes(reportId),
    [topReportIds]
  );

  // Get usage count for a report
  const getUsageCount = useCallback(
    (reportId: string) => usageData[reportId] || 0,
    [usageData]
  );

  // Get top reports with full details
  const getTopReportsWithDetails = useCallback(() => {
    const allReports = getAllReports();
    return topReportIds
      .map((id) => allReports.find((r) => r.id === id))
      .filter(Boolean) as Array<ReportItem & { sectionColor: string; sectionBgColor: string }>;
  }, [topReportIds]);

  // Filter reports based on search term
  const filterReports = useCallback(
    (reports: ReportItem[]) => {
      if (!searchTerm.trim()) return reports;
      const lowerSearch = searchTerm.toLowerCase();
      return reports.filter(
        (report) =>
          report.title.toLowerCase().includes(lowerSearch) ||
          report.description.toLowerCase().includes(lowerSearch)
      );
    },
    [searchTerm]
  );

  // Get filtered sections (only sections that have matching reports)
  const filteredSections = useMemo(() => {
    if (!searchTerm.trim()) return reportSections;
    return reportSections
      .map((section) => ({
        ...section,
        reports: filterReports(section.reports),
      }))
      .filter((section) => section.reports.length > 0);
  }, [searchTerm, filterReports]);

  // Get filtered top reports
  const filteredTopReports = useMemo(() => {
    const topReports = getTopReportsWithDetails();
    if (!searchTerm.trim()) return topReports;
    const lowerSearch = searchTerm.toLowerCase();
    return topReports.filter(
      (report) =>
        report.title.toLowerCase().includes(lowerSearch) ||
        report.description.toLowerCase().includes(lowerSearch)
    );
  }, [getTopReportsWithDetails, searchTerm]);

  const reportsBreadcrumbs = [
    {
      title: "Reports",
      path: "/reports",
      isActive: true,
    },
  ];

  return (
    <>
      <PageTitle breadcrumbs={reportsBreadcrumbs}>All Reports</PageTitle>

      {/* Search Bar */}
      <div className="card mb-5">
        <div className="card-body py-4">
          <div className="position-relative">
            <span className="position-absolute d-flex align-items-center top-50 translate-middle-y ms-4">
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#A1A5B7"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <circle cx="11" cy="11" r="8" />
                <path d="M21 21l-4.35-4.35" />
              </svg>
            </span>
            <input
              type="text"
              className="form-control form-control-lg ps-14"
              placeholder="Search reports by name or description..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{ paddingLeft: "50px" }}
            />
            {searchTerm && (
              <button
                type="button"
                className="btn btn-sm btn-icon position-absolute top-50 end-0 translate-middle-y me-2"
                onClick={() => setSearchTerm("")}
                style={{ opacity: 0.5 }}
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                >
                  <path d="M18 6L6 18M6 6l12 12" />
                </svg>
              </button>
            )}
          </div>
          {searchTerm && (
            <div className="mt-3 text-muted fs-7">
              Found {filteredSections.reduce((acc, s) => acc + s.reports.length, 0)} report(s)
              {filteredTopReports.length > 0 && ` including ${filteredTopReports.length} frequently used`}
            </div>
          )}
        </div>
      </div>

      {/* Frequently Used Section */}
      {filteredTopReports.length > 0 && (
        <div className="card mb-5 overflow-hidden">
          <div
            className="position-relative"
            style={{
              backgroundColor: isDark ? "#1e1e2d" : "#FFF8DD",
              minHeight: "100px",
              overflow: "hidden",
            }}
          >
            {/* Background SVG */}
            <div
              className="position-absolute d-flex align-items-center justify-content-end"
              style={{
                right: "-20px",
                top: "-30px",
                bottom: "-30px",
                width: "60%",
                color: "#FFC700",
                pointerEvents: "none",
              }}
            >
              <svg viewBox="0 0 400 200" fill="none" style={{ width: "400px", height: "220px", opacity: 0.12 }}>
                <path d="M200 20l44 90 100 14-72 70 16 100-88-46-88 46 16-100-72-70 100-14z" fill="currentColor"/>
                <circle cx="350" cy="50" r="50" fill="currentColor"/>
                <circle cx="100" cy="150" r="70" fill="currentColor"/>
                <circle cx="300" cy="170" r="40" fill="currentColor"/>
              </svg>
            </div>

            {/* Header Content */}
            <div
              className="d-flex align-items-center justify-content-between px-9 position-relative"
              style={{ zIndex: 2, paddingTop: "28px", paddingBottom: "40px" }}
            >
              <div className="d-flex align-items-center">
                <svg
                  width="28"
                  height="28"
                  viewBox="0 0 24 24"
                  fill="#FFC700"
                  className="me-2"
                >
                  <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
                </svg>
                <h3 className="fw-bold m-0 fs-2" style={{ color: isDark ? "#FFC700" : "#B7950B" }}>
                  Frequently Used
                </h3>
              </div>
              <span className="text-muted fs-7">
                Top {filteredTopReports.length} reports
              </span>
            </div>

            {/* Wave SVG */}
            <svg
              viewBox="0 0 1440 60"
              fill="none"
              preserveAspectRatio="none"
              style={{
                position: "absolute",
                bottom: "0",
                left: 0,
                width: "100%",
                height: "30px",
                zIndex: 1,
              }}
            >
              <path
                d="M0 30C360 60 720 0 1080 30C1260 45 1350 45 1440 30V60H0V60Z"
                fill={isDark ? "var(--bs-card-bg, #1e1e2d)" : "white"}
              />
            </svg>
          </div>

          <div className="card-body py-3">
            <div className="row g-3">
              {filteredTopReports.map((report, index) => (
                <div key={report.id} className="col-xl col-lg-4 col-md-6">
                  <div
                    className="d-flex flex-column align-items-center p-4 rounded-3 cursor-pointer h-100 frequent-report-card"
                    style={{
                      backgroundColor: isDark ? "rgba(30,30,45,0.6)" : report.sectionBgColor,
                      border: `1px solid transparent`,
                      transition: "all 0.15s ease",
                    }}
                    onClick={() => handleReportClick(report.path)}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.borderColor = report.sectionColor;
                      e.currentTarget.style.transform = "translateY(-2px)";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.borderColor = "transparent";
                      e.currentTarget.style.transform = "translateY(0)";
                    }}
                  >
                    {/* Rank Badge */}
                    <div
                      className="position-absolute"
                      style={{
                        top: "8px",
                        left: "8px",
                        width: "20px",
                        height: "20px",
                        borderRadius: "50%",
                        backgroundColor: report.sectionColor,
                        color: "#fff",
                        fontSize: "11px",
                        fontWeight: 600,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      {index + 1}
                    </div>

                    {/* Icon */}
                    <div
                      className="d-flex align-items-center justify-content-center rounded-circle mb-3"
                      style={{
                        width: "48px",
                        height: "48px",
                        backgroundColor: isDark ? "rgba(255,255,255,0.1)" : "#fff",
                        color: report.sectionColor,
                      }}
                    >
                      {report.icon}
                    </div>

                    {/* Title */}
                    <span
                      className="fw-semibold text-center fs-6"
                      style={{ color: report.sectionColor }}
                    >
                      {report.title}
                    </span>

                    {/* Usage Count */}
                    <span className="text-muted fs-8 mt-1">
                      {getUsageCount(report.id)} views
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* No Results Message */}
      {searchTerm && filteredSections.length === 0 && filteredTopReports.length === 0 && (
        <div className="card">
          <div className="card-body py-12 text-center">
            <svg
              width="64"
              height="64"
              viewBox="0 0 24 24"
              fill="none"
              stroke="#A1A5B7"
              strokeWidth="1.5"
              className="mb-4"
            >
              <circle cx="11" cy="11" r="8" />
              <path d="M21 21l-4.35-4.35" />
              <path d="M8 8l6 6M14 8l-6 6" />
            </svg>
            <h4 className="text-gray-600 fw-semibold mb-2">No reports found</h4>
            <p className="text-gray-500 fs-6 mb-0">
              No reports match "{searchTerm}". Try a different search term.
            </p>
          </div>
        </div>
      )}

      {/* Report Sections */}
      <div className="d-flex flex-column gap-5">
        {filteredSections.map((section) => (
          <div key={section.id}>
            <div
              className="card section-card overflow-hidden"
              style={{
                borderTop: `3px solid ${section.color}`,
              }}
            >
              {/* Section Header with Background SVG and Wave */}
              <div
                className="position-relative"
                style={{
                  backgroundColor: isDark ? "rgba(30,30,45,0.8)" : section.bgColor,
                  minHeight: "100px",
                  overflow: "hidden",
                }}
              >
                {/* Background Decorative SVG */}
                <div
                  className="position-absolute d-flex align-items-center justify-content-end"
                  style={{
                    right: "-20px",
                    top: "-30px",
                    bottom: "-30px",
                    width: "60%",
                    color: section.color,
                    pointerEvents: "none",
                  }}
                >
                  {section.backgroundSvg}
                </div>

                {/* Header Content */}
                <div
                  className="d-flex align-items-center justify-content-between px-9 position-relative"
                  style={{ zIndex: 2, paddingTop: "28px", paddingBottom: "40px" }}
                >
                  {/* Title */}
                  <h3 className="fw-bold m-0 fs-2" style={{ color: section.color }}>
                    {section.title}
                  </h3>

                  {/* Report Count */}
                  <span className="text-muted fs-7">
                    {section.reports.length} {section.reports.length === 1 ? "report" : "reports"}
                  </span>
                </div>

                {/* Wave SVG */}
                <svg
                  viewBox="0 0 1440 60"
                  fill="none"
                  preserveAspectRatio="none"
                  style={{
                    position: "absolute",
                    bottom: "0",
                    left: 0,
                    width: "100%",
                    height: "30px",
                    zIndex: 1,
                  }}
                >
                  <path
                    d="M0 30C360 60 720 0 1080 30C1260 45 1350 45 1440 30V60H0V30Z"
                    fill={isDark ? "var(--bs-card-bg, #1e1e2d)" : "white"}
                  />
                </svg>
              </div>

              {/* Reports List */}
              <div className="card-body pt-4 pb-3">
                {section.reports.map((report, index) => {
                  const usageCount = getUsageCount(report.id);
                  const isTop = isTopReport(report.id);

                  return (
                    <div
                      key={report.id}
                      className="report-item d-flex align-items-center py-3 px-3 mx-n3 rounded cursor-pointer"
                      style={{
                        borderBottom:
                          index < section.reports.length - 1 ? "1px solid var(--bs-gray-200)" : "none",
                      }}
                      onClick={() => handleReportClick(report.path)}
                    >
                      {/* Icon */}
                      <div
                        className="d-flex align-items-center justify-content-center rounded-2 me-3 flex-shrink-0"
                        style={{
                          width: "42px",
                          height: "42px",
                          backgroundColor: isDark ? "rgba(255,255,255,0.05)" : section.bgColor,
                          color: section.color,
                        }}
                      >
                        {report.icon}
                      </div>

                      {/* Content */}
                      <div className="flex-grow-1 min-w-0">
                        <div className="d-flex align-items-center">
                          <span className="text-gray-900 fw-semibold fs-6">
                            {report.title}
                          </span>
                          {/* Star for top reports */}
                          {isTop && (
                            <svg
                              width="14"
                              height="14"
                              viewBox="0 0 24 24"
                              fill="#FFC700"
                              className="ms-2"
                            >
                              <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
                            </svg>
                          )}
                        </div>
                        <span className="text-gray-500 fs-7 text-truncate d-block">
                          {report.description}
                        </span>
                      </div>

                      {/* Usage count and Arrow */}
                      <div className="flex-shrink-0 ms-2 d-flex align-items-center">
                        {usageCount > 0 && (
                          <span
                            className="badge me-3 fs-8"
                            style={{
                              backgroundColor: isDark ? "rgba(255,255,255,0.05)" : section.bgColor,
                              color: section.color,
                              minWidth: "28px",
                            }}
                          >
                            {usageCount}
                          </span>
                        )}
                        <i
                          className="bi bi-chevron-right fs-6"
                          style={{ color: section.color, opacity: 0.5 }}
                        ></i>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        ))}
      </div>

      <style>{`
        .cursor-pointer {
          cursor: pointer;
        }
        .section-card {
          transition: box-shadow 0.2s ease;
        }
        .section-card:hover {
          box-shadow: 0 0.5rem 1.5rem 0.5rem rgba(0, 0, 0, 0.06);
        }
        .report-item {
          transition: background-color 0.15s ease;
        }
        .report-item:hover {
          background-color: var(--bs-gray-100);
        }
        .report-item:active {
          background-color: var(--bs-gray-200);
        }
        .frequent-report-card {
          position: relative;
        }
        .frequent-report-card:active {
          transform: scale(0.98) !important;
        }
        .fs-9 {
          font-size: 0.7rem !important;
        }
        .ms-8 {
          margin-left: 2rem !important;
        }
        .ps-14 {
          padding-left: 3.5rem !important;
        }
        .report-page-search .form-control:focus {
          border-color: #009EF7;
          box-shadow: 0 0 0 0.2rem rgba(0, 158, 247, 0.15);
        }
      `}</style>
    </>
  );
};

export { AllReportsPage };
export default AllReportsPage;
